import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Email, FingerprintRecognition } from '@core/icons-react';
import { PageCard, PrimaryButton, Checkbox, TextInput, Link } from '@core/ui';
import { validateEmail } from '../../../utils/validation';
function parseAuthError(errorMessage) {
    const errorLower = errorMessage.toLowerCase();
    if (errorLower.includes('verification') || errorLower.includes('code') ||
        errorLower.includes('invalid') || errorLower.includes('incorrect')) {
        return {
            kind: 'error',
            title: 'Incorrect Code',
            subtitle: 'Check and try again'
        };
    }
    if (errorLower.includes('network') || errorLower.includes('connection') || errorLower.includes('fetch')) {
        return {
            kind: 'error',
            title: 'Network Error',
            subtitle: 'Check your connection'
        };
    }
    if (errorLower.includes('expired') || errorLower.includes('session')) {
        return {
            kind: 'error',
            title: 'Session Expired',
            subtitle: 'Refresh to continue'
        };
    }
    if (errorLower.includes('too many') || errorLower.includes('rate limit')) {
        return {
            kind: 'warning',
            title: 'Too Many Attempts',
            subtitle: 'Wait before retrying'
        };
    }
    if (errorLower.includes('passkey')) {
        return {
            kind: 'error',
            title: 'Passkey Failed',
            subtitle: 'Try different method'
        };
    }
    return {
        kind: 'error',
        title: 'Authentication Failed',
        subtitle: 'Try again'
    };
}
export function LoginCard({ onSubmit, onVerifyOTP, onSelectMethod, onClearError, noCard = false, error = null, initialEmail = '' }) {
    const [email, setEmail] = useState(initialEmail);
    const [rememberMe, setRememberMe] = useState(false);
    const [showError, setShowError] = useState(false);
    const [showOTPInput, setShowOTPInput] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [otpError, setOtpError] = useState(false);
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);
        // Clear error when input is empty
        if (value === '') {
            setShowError(false);
        }
        else if (showError) {
            // Revalidate if there was an error shown
            setShowError(!validateEmail(value));
        }
    };
    const handleEmailKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleMethodClick('email');
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Trigger the same flow as clicking "Continue with Email" button
        handleMethodClick('email');
    };
    const handleOTPChange = (e) => {
        const value = e.target.value;
        // Clear server error when user starts typing
        if (onClearError && error) {
            onClearError();
        }
        // Limit to 6 characters and only allow digits
        if (value.length <= 6 && /^\d*$/.test(value)) {
            setOtpCode(value);
            setOtpError(false);
        }
        else if (!/^\d*$/.test(value)) {
            setOtpError(true);
        }
    };
    const handleOTPSubmit = async () => {
        if (otpCode.length === 6 && /^\d{6}$/.test(otpCode)) {
            // Submit OTP for verification
            if (onVerifyOTP) {
                onVerifyOTP(otpCode, rememberMe);
            }
            else {
                onSubmit(email, rememberMe);
            }
        }
        else {
            setOtpError(true);
        }
    };
    const handleOTPKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleOTPSubmit();
        }
    };
    const handleMethodClick = (methodId) => {
        // Only validate email for methods that require it (not passkey)
        if (methodId !== 'passkey' && !validateEmail(email)) {
            setShowError(true);
            return;
        }
        setShowError(false);
        if (methodId === 'email') {
            // Show OTP input immediately
            setShowOTPInput(true);
            // Trigger OTP send
            if (onSelectMethod) {
                onSelectMethod('code', email, rememberMe);
            }
            else {
                onSubmit(email, rememberMe);
            }
        }
        else if (methodId === 'passkey') {
            // Passkey authentication doesn't require email entry
            // The passkey itself contains the user identifier
            if (onSelectMethod) {
                onSelectMethod('passkey', undefined, rememberMe);
            }
            else {
                onSubmit(email, rememberMe);
            }
        }
        else if (methodId === 'idp') {
            // Identity provider handled by parent
            if (onSelectMethod) {
                onSelectMethod('idp', undefined, rememberMe);
            }
        }
        else {
            // Other methods
            onSubmit(email, rememberMe);
        }
    };
    const mainContent = (_jsxs(_Fragment, { children: [showOTPInput ? (_jsxs("div", { style: {
                    padding: '0',
                    opacity: 1,
                    transition: 'opacity 0.5s ease-in-out'
                }, children: [_jsxs("div", { style: { marginBottom: '0.75rem' }, children: [_jsx("div", { className: error && !otpError ? 'otp-server-error' : '', children: _jsx(TextInput, { id: "otp-input", labelText: "Enter verification code", helperText: _jsxs(_Fragment, { children: ["Code sent to ", _jsx("strong", { children: email })] }), placeholder: "Enter 6-digit code", value: otpCode, onChange: handleOTPChange, onKeyDown: handleOTPKeyDown, type: "text", size: "md", invalid: otpError, invalidText: "Please enter 6 digits (numbers only)", maxLength: 6, style: { textAlign: 'center' }, autoFocus: true, autoComplete: "one-time-code", inputMode: "numeric", pattern: "[0-9]{6}", layer: 2, "aria-label": "Enter 6-digit verification code", hideErrorIcon: true }) }), _jsx("style", { children: `
              .otp-server-error input {
                border-color: var(--core-support-error) !important;
                outline-color: var(--core-support-error) !important;
              }
            ` }), _jsx("div", { style: { marginTop: '0.5rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }, children: _jsx(Link, { href: "#", onClick: (e) => {
                                        e.preventDefault();
                                        setShowOTPInput(false);
                                        setOtpCode('');
                                        setOtpError(false);
                                    }, children: "Use different method" }) })] }), _jsx(PrimaryButton, { variant: "default", size: "small", title: "Verify", onClick: handleOTPSubmit, isDisabled: false, fullWidth: true, align: "center" })] })) : (_jsx("div", { style: {
                    padding: '0',
                    opacity: 1,
                    transition: 'opacity 0.5s ease-in-out'
                }, children: _jsxs("form", { onSubmit: handleSubmit, children: [_jsx(TextInput, { id: "email", labelText: "Email", placeholder: "Enter your email", value: email, onChange: handleEmailChange, onKeyDown: handleEmailKeyDown, invalid: showError, invalidText: "Please enter a valid email address", autoComplete: "username webauthn", size: "lg", layer: 2 }), _jsx("div", { style: { marginTop: '25px' }, children: _jsx(Checkbox, { id: "remember-me", label: "Remember me on this browser", checked: rememberMe, onChange: (e) => setRememberMe(e.target.checked), variant: "with-label" }) })] }) })), _jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    margin: '1.5rem 0',
                    color: 'var(--core-text-secondary)',
                    height: '1.1875rem'
                }, children: [_jsx("div", { style: { flex: 1, height: '1px', backgroundColor: 'var(--core-border-base)' } }), _jsx("span", { style: { padding: '0 0.75rem', fontSize: '0.65rem' }, children: "AND" }), _jsx("div", { style: { flex: 1, height: '1px', backgroundColor: 'var(--core-border-base)' } })] }), _jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem' }, children: [_jsx(PrimaryButton, { icon: Email, title: "Continue with Email", variant: "default", onClick: () => handleMethodClick('email'), isDisabled: showOTPInput, fullWidth: true, align: "spread", iconPosition: "right" }), _jsx(PrimaryButton, { icon: FingerprintRecognition, title: "Continue with Passkey", variant: "default", onClick: () => handleMethodClick('passkey'), isDisabled: showOTPInput, fullWidth: true, align: "spread", iconPosition: "right" })] })] }));
    const footerDescription = (_jsx(_Fragment, { children: "Contact support for authentication issues." }));
    if (noCard) {
        return (_jsxs("div", { className: "login-card-content", children: [_jsx("div", { style: { padding: '1.5rem 1rem' }, children: mainContent }), _jsx("div", { style: {
                        height: '1px',
                        backgroundColor: 'var(--core-border-base)',
                        width: '100%'
                    } }), _jsx("div", { style: {
                        padding: '1rem',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 400,
                        color: 'var(--core-text-secondary)'
                    }, children: footerDescription })] }));
    }
    const alertConfig = error ? (() => {
        const parsed = parseAuthError(error);
        return {
            kind: parsed.kind,
            title: parsed.title,
            subtitle: parsed.subtitle,
            type: 'toast'
        };
    })() : undefined;
    return (_jsx(PageCard, { heading: "Log In", className: "login-card-480", width: 480, shadow: true, padding: false, showDividers: true, showFooter: true, footerContent: _jsx("div", { className: "core-page-card-footer-description", children: footerDescription }), contentPadding: "1.5rem 1rem", alert: alertConfig, onAlertClose: onClearError, children: mainContent }));
}
